/**
 * Service Delivery overview.
 *
 * Answers one question in order: what needs my attention today, and are these
 * clients profitable? Manager and above — the revenue block carries labour cost.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ServiceDashboard } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle, Clock, Ban, Timer, FileCheck, CalendarClock, ArrowRight,
} from 'lucide-react';

export function ServiceOverview() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'dashboard'],
    queryFn: () => api.getServiceDashboard(),
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const d = data?.data as ServiceDashboard | undefined;
  const a = d?.needsAttention;
  const r = d?.revenueAndLabor;

  // Hours delivered by someone with no labour rate cost nothing in the rollup,
  // which makes gross contribution equal the whole fee. Rather than print a
  // flattering number with a footnote, the figure is withheld until every
  // delivery hour is priced.
  const unpriced = Number(r?.unpriced_minutes ?? 0);
  const unratedPeople = Number(r?.users_without_rate ?? 0);

  const attention = [
    { label: 'Overdue work', value: a?.overdue_work_items ?? 0, icon: <AlertTriangle className="h-4 w-4" />, to: 'work?overdueOnly=true' },
    { label: 'Awaiting review', value: a?.awaiting_review ?? 0, icon: <FileCheck className="h-4 w-4" />, to: 'review' },
    { label: 'Blocked', value: a?.blocked_work_items ?? 0, icon: <Ban className="h-4 w-4" />, to: 'work?status=blocked' },
    { label: 'Unapproved time', value: a?.unapproved_time_entries ?? 0, icon: <Timer className="h-4 w-4" />, to: 'time' },
    { label: 'Pending authorizations', value: a?.pending_authorizations ?? 0, icon: <Clock className="h-4 w-4" />, to: 'profitability' },
    { label: 'Cycles closing soon', value: a?.cycles_closing_soon ?? 0, icon: <CalendarClock className="h-4 w-4" />, to: 'engagements' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-semibold tracking-tight">Service Delivery</h1>
        <p className="text-sm text-muted-foreground mt-1">
          What needs attention today, and how the current period is running.
        </p>
      </div>

      {/* 1. Needs attention */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {attention.map((x) => (
          <button key={x.label} onClick={() => navigate(x.to)} className="text-left">
            <Card className={x.value > 0 ? 'border-destructive/30' : undefined}>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{x.label}</span>
                  <span className={x.value > 0 ? 'text-destructive' : 'text-muted-foreground'}>{x.icon}</span>
                </div>
                <p className={`text-2xl font-semibold mt-1 ${x.value > 0 ? 'text-destructive' : ''}`}>
                  {x.value}
                </p>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {/* 4. Revenue and labour — stated carefully on purpose */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Current period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            <Figure label="Contracted revenue" value={formatCurrency(r?.contracted_revenue ?? 0)} />
            <Figure label="Direct labour cost" value={formatCurrency(r?.direct_labor_cost ?? 0)} />
            <Figure
              label="Gross contribution"
              value={unpriced > 0 ? '—' : formatCurrency(r?.gross_contribution ?? 0)}
              hint={unpriced > 0 ? 'Not calculable yet' : 'Before overhead'}
              muted={unpriced > 0}
            />
            <Figure
              label="Hours"
              value={`${Number(r?.actual_hours ?? 0).toFixed(1)} / ${Number(r?.planned_hours ?? 0).toFixed(1)}`}
              hint="Actual vs planned"
            />
            <Figure
              label="Revenue per delivery hour"
              value={r?.revenue_per_delivery_hour != null ? formatCurrency(r.revenue_per_delivery_hour) : '—'}
            />
          </div>
          {unpriced > 0 && (
            <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 px-3 py-2.5">
              <p className="text-xs text-amber-900 dark:text-amber-200">
                <strong>{(unpriced / 60).toFixed(2)} delivery hours have no labour rate.</strong>{' '}
                Those hours cost nothing in the figures above, so gross contribution would be
                overstated by their real cost. It is withheld until they are priced
                {unratedPeople > 0
                  ? ` — ${unratedPeople} active ${unratedPeople === 1 ? 'person has' : 'people have'} no rate set.`
                  : '.'}
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4 border-t pt-3">
            Gross contribution is revenue minus <strong>direct labour only</strong>. Payroll taxes,
            software, management time and overhead are not included, so it is not profit.
          </p>
        </CardContent>
      </Card>

      {/* 2. Client health */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Client health</CardTitle></CardHeader>
        <CardContent className="p-0">
          {(d?.clientHealth ?? []).length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No active engagements yet.
            </p>
          ) : (
            <ul className="divide-y">
              {d!.clientHealth.map((c) => {
                const done = c.work_item_count > 0 ? (c.completed_count / c.work_item_count) * 100 : 0;
                const util = c.utilization != null ? c.utilization * 100 : null;
                return (
                  <li key={c.engagement_id}>
                    <button
                      onClick={() => navigate(`engagements/${c.engagement_id}`)}
                      className="w-full text-left px-6 py-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{c.account_name}</p>
                            <span className="text-xs text-muted-foreground">{c.service_line_name}</span>
                            {c.overdue_count > 0 && (
                              <Badge variant="destructive">{c.overdue_count} overdue</Badge>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <Progress value={done} className="h-1.5 w-40" />
                            <span className="text-xs text-muted-foreground">
                              {c.completed_count}/{c.work_item_count} done
                              {c.current_cycle_label ? ` · ${c.current_cycle_label}` : ''}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-medium">
                            {c.monthly_price != null ? formatCurrency(c.monthly_price) : '—'}
                          </p>
                          <p className={`text-xs ${util != null && util > 110 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {util != null ? `${util.toFixed(0)}% of planned hours` : 'no plan hours'}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* 3. Today's workload */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Today's workload</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y">
            {(d?.workload ?? []).map((w) => (
              <li key={w.user_id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">{w.user_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground capitalize">{w.role}</span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-muted-foreground">{w.open_items} open</span>
                  <span className="text-muted-foreground">{w.due_today} due today</span>
                  <span className="tabular-nums">{Number(w.hours_logged_today ?? 0).toFixed(2)}h logged</span>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Figure({ label, value, hint, muted }: {
  label: string; value: string; hint?: string; muted?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold mt-0.5 tabular-nums ${muted ? 'text-muted-foreground' : ''}`}>
        {value}
      </p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
